import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bulkAPI, API_BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PublicLayout from '../components/PublicLayout';
import Seo from '../components/Seo';
import BulkCardMedia from '../components/BulkCardMedia';
import { ProductListSkeleton, EmptyState, ErrorState } from '../components/skeletons';
import { renderIcon } from './admin/TaxonomyManager';

const API_BASE = API_BASE_URL;

const cleanIcon = (icon) => (icon && typeof icon === 'string' && !icon.startsWith('http') ? `${icon} ` : '');

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
};

export default function BulkListings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [search, setSearch] = useState('');
  const [bidAmounts, setBidAmounts] = useState({});
  const [bidQtys, setBidQtys] = useState({});
  const [bidLoading, setBidLoading] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);
  const [detail, setDetail] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [bidErrors, setBidErrors] = useState({});

  const fetchListings = () => {
    setLoading(true);
    setHasError(false);
    bulkAPI.listListings({ limit: 50 })
      .then(r => setListings(r.data.data || []))
      .catch(() => setHasError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const updateBid = (id, field, val) => {
    const listing = listings.find(l => l._id === id) || detail;
    if (!listing) return;

    const nextPrice = field === 'price' ? val : (bidAmounts[id] || '');
    const nextQty = field === 'qty' ? val : (bidQtys[id] || '1');

    if (field === 'price') setBidAmounts(p => ({ ...p, [id]: val }));
    else setBidQtys(p => ({ ...p, [id]: val }));

    let err = '';
    const p = Number(nextPrice);
    const q = Number(nextQty);
    const available = listing.stock - (listing.sold || 0);

    if (nextPrice && p > listing.priceCeiling) err = `Max price is ₹${listing.priceCeiling.toLocaleString('en-IN')}`;
    else if (nextPrice && p > 0 && p < listing.priceFloor) err = `Min price is ₹${listing.priceFloor.toLocaleString('en-IN')}`;
    else if (nextQty && q > available) err = `Only ${available} units available`;
    else if (nextQty && q <= 0) err = `Quantity must be at least 1`;

    setBidErrors(p => ({ ...p, [id]: err }));
  };

  const handleBid = async (listingId) => {
    if (!user) { navigate('/login'); return; }
    const listing = listings.find(l => l._id === listingId) || detail;
    if (!listing) return;

    const priceStr = bidAmounts[listingId] || '';
    const qtyStr = bidQtys[listingId] || '1';
    const price = Number(priceStr);
    const quantity = Number(qtyStr);

    // Final validation before submit
    let err = '';
    const available = listing.stock - (listing.sold || 0);

    if (!price || price <= 0) err = 'Enter a valid price';
    else if (price < listing.priceFloor || price > listing.priceCeiling) {
      err = `Price must be ₹${listing.priceFloor.toLocaleString('en-IN')} - ₹${listing.priceCeiling.toLocaleString('en-IN')}`;
    } else if (quantity > available) {
      err = `Only ${available} available`;
    } else if (quantity <= 0) {
      err = `Enter valid quantity`;
    }

    if (err) {
      setBidErrors(p => ({ ...p, [listingId]: err }));
      return;
    }

    setBidLoading(listingId);
    try {
      await bulkAPI.placeBid({ bulkListingId: listingId, bidPrice: price, qty: quantity });
      setBidSuccess(listingId);
      setBidAmounts(p => ({ ...p, [listingId]: '' }));
      setBidQtys(p => ({ ...p, [listingId]: '' }));
      setBidErrors(p => ({ ...p, [listingId]: '' }));
      setTimeout(() => setBidSuccess(null), 3000);
    } catch (err) {
      setBidErrors(p => ({ ...p, [listingId]: err.response?.data?.message || 'Failed to place bid' }));
    } finally { setBidLoading(null); }
  };

  const compileSearchRegex = (s) => {
    if (!s) return null;
    const stripped = s.trim().replace(/\s+/g, '');
    if (!stripped) return null;
    const chars = [...stripped].map(char => char.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
    return new RegExp(chars.join('[\\s\\-_]*'), 'i');
  };

  const searchRegex = compileSearchRegex(search);

  const filtered = searchRegex
    ? listings.filter(l =>
        searchRegex.test(l.title || '') ||
        searchRegex.test(l.categoryId?.name || '') ||
        searchRegex.test(l.brandId?.name || ''))
    : listings;

  return (
    <PublicLayout>
      <Seo title="Bulk Sales — Buy at Wholesale Prices" description="Bid on bulk product listings from verified vendors. Get the best wholesale prices in India." />
      <div style={{ background: '#FAF8F3', minHeight: 'calc(100vh - 64px)', padding: '24px 0 60px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
          {/* Title + Search */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 700, color: '#1A1A1A', margin: 0 }}>Bulk Sales</h1>
              <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{filtered.length} listings available · Bid to buy at your price</p>
            </div>
            <input placeholder="Search products, brands..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, width: '100%', maxWidth: 280 }} />
          </div>

          {loading ? (
            <ProductListSkeleton count={6} columns={3} />
          ) : hasError ? (
            <ErrorState onRetry={fetchListings} />
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No bulk listings yet"
              description="Check back later or register as a vendor to list your products for bulk sales."
              actionLabel={user?.role === 'vendor' ? 'Create Bulk Listing' : 'Become a Vendor'}
              onAction={() => navigate(user?.role === 'vendor' ? '/vendor/bulk' : '/vendor/signup')}
            />
          ) : (
            <div className="grid-1-2-3" style={{ gap: 18 }}>
              {filtered.map(l => {
                const defMedia = l.media?.find(m => m.isDefault) || l.media?.[0];
                return (
                  <div key={l._id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* Interactive Media Slider */}
                    <BulkCardMedia 
                      listing={l} 
                      height={180} 
                      getMediaUrl={getMediaUrl} 
                      onDetailClick={() => { setDetail(l); setGalleryIdx(0); }} 
                    />


                    <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Title */}
                      <h3 onClick={() => { setDetail(l); setGalleryIdx(0); }} style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', margin: '0 0 6px', cursor: 'pointer' }}>{l.title}</h3>

                      {/* Category badges */}
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                        {l.categoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#E8F5EE', color: '#1B5C3A' }}>{cleanIcon(l.categoryId.icon)}{l.categoryId.name}</span>}
                        {l.subCategoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}>{l.subCategoryId.name}</span>}
                        {l.brandId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#FFF8E7', color: '#92400E', display: 'inline-flex', alignItems: 'center' }}>{renderIcon(l.brandId.icon, { width: 14, height: 14, marginRight: 4 })}{l.brandId.name}</span>}
                      </div>

                      {l.description && <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, marginBottom: 10 }}>{l.description.length > 80 ? l.description.slice(0, 80) + '...' : l.description}</p>}

                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1B5C3A', marginBottom: 12 }}>
                        ₹{l.priceFloor?.toLocaleString('en-IN')} - ₹{l.priceCeiling?.toLocaleString('en-IN')}
                        <span style={{ fontSize: 11, fontWeight: 400, color: '#6B7280', marginLeft: 4 }}>/ unit</span>
                      </div>

                       {/* Stock */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                        <div style={{ background: '#F3F4F6', borderRadius: 8, padding: '6px 12px', width: '100%', textAlign: 'center' }}>
                          <span style={{ fontSize: 10, color: '#6B7280' }}>Stock: </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: (l.stock - (l.sold || 0)) < 10 ? '#DC2626' : '#1A1A1A' }}>{l.stock - (l.sold || 0)} units</span>
                        </div>
                      </div>

                      {/* Bid section */}
                      <div style={{ marginTop: 'auto', borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
                        {bidSuccess === l._id ? (
                          <div style={{ background: '#E8F5EE', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1B5C3A', fontWeight: 600, textAlign: 'center' }}>
                            ✅ Bid placed successfully!
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input type="number" placeholder="₹ Price" value={bidAmounts[l._id] || ''}
                              onChange={e => updateBid(l._id, 'price', e.target.value)}
                              style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${bidErrors[l._id] ? '#dc2626' : '#E5E7EB'}`, fontSize: 13 }} />
                            <input type="number" placeholder="Qty" value={bidQtys[l._id] || ''}
                              onChange={e => updateBid(l._id, 'qty', e.target.value)}
                              style={{ width: 60, padding: '8px 10px', borderRadius: 8, border: `1px solid ${bidErrors[l._id] ? '#dc2626' : '#E5E7EB'}`, fontSize: 13 }} />
                            <button onClick={() => handleBid(l._id)} disabled={bidLoading === l._id}
                              style={{ background: '#1B5C3A', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: bidLoading === l._id ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                              {bidLoading === l._id ? '...' : 'Bid'}
                            </button>
                          </div>
                        )}
                        {bidErrors[l._id] && <div style={{ color: '#dc2626', fontSize: 10, marginTop: 4, fontWeight: 600 }}>{bidErrors[l._id]}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Modal with Gallery */}
        {detail && (() => {
          const media = detail.media || [];
          const current = media[galleryIdx];
          return (
            <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 700, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                {/* Gallery */}
                {media.length > 0 ? (
                  <div style={{ position: 'relative', background: '#111', borderRadius: '18px 18px 0 0', overflow: 'hidden' }}>
                    <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {current?.type === 'video' ? (
                        <video src={getMediaUrl(current.url)} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      ) : current ? (
                        <img src={getMediaUrl(current.url)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      ) : null}
                    </div>
                    {/* Nav arrows */}
                    {media.length > 1 && (
                      <>
                        <button onClick={() => setGalleryIdx(i => (i - 1 + media.length) % media.length)}
                          style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer', fontWeight: 700 }}>‹</button>
                        <button onClick={() => setGalleryIdx(i => (i + 1) % media.length)}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer', fontWeight: 700 }}>›</button>
                      </>
                    )}
                    {/* Counter */}
                    <div style={{ position: 'absolute', bottom: 10, right: 14, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8 }}>
                      {galleryIdx + 1} / {media.length}
                    </div>
                    {/* Close */}
                    <button onClick={() => setDetail(null)}
                      style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 16, cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ height: 200, background: '#F3F4F6', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64 }}>📦</div>
                )}

                {/* Thumbnails */}
                {media.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, padding: '10px 18px', overflowX: 'auto', background: '#fafafa' }}>
                    {media.map((m, i) => (
                      <div key={i} onClick={() => setGalleryIdx(i)}
                        style={{ width: 56, height: 56, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: i === galleryIdx ? '2px solid #1B5C3A' : '1px solid #E5E7EB', flexShrink: 0, opacity: i === galleryIdx ? 1 : 0.6 }}>
                        {m.type === 'video'
                          ? <video src={getMediaUrl(m.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                          : <img src={getMediaUrl(m.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        }
                      </div>
                    ))}
                  </div>
                )}

                {/* Info */}
                <div style={{ padding: '18px 22px' }}>
                  <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 8px' }}>{detail.title}</h2>

                  <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                    {detail.categoryId?.name && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#E8F5EE', color: '#1B5C3A' }}>{cleanIcon(detail.categoryId.icon)}{detail.categoryId.name}</span>}
                    {detail.subCategoryId?.name && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}>{detail.subCategoryId.name}</span>}
                    {detail.brandId?.name && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#FFF8E7', color: '#92400E', display: 'inline-flex', alignItems: 'center' }}>{renderIcon(detail.brandId.icon, { width: 14, height: 14, marginRight: 4 })}{detail.brandId.name}</span>}
                  </div>

                  {detail.description && <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.7, marginBottom: 14 }}>{detail.description}</p>}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: '#F0FDF4', borderRadius: 10, padding: 14, textAlign: 'center', border: '1px solid #DCFCE7' }}>
                      <div style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>Expected Price Range (per unit)</div>
                      <div style={{ fontWeight: 800, fontSize: 22, color: '#1B5C3A', marginTop: 6 }}>
                        ₹{detail.priceFloor?.toLocaleString('en-IN')} - ₹{detail.priceCeiling?.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div style={{ background: '#F9FAFB', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#6B7280' }}>Available Stock</div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: '#1A1A1A', marginTop: 4 }}>{detail.stock - (detail.sold || 0)} units</div>
                    </div>
                  </div>

                  {/* Bid */}
                  <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 14 }}>
                    {bidSuccess === detail._id ? (
                      <div style={{ background: '#E8F5EE', borderRadius: 10, padding: 14, fontSize: 14, color: '#1B5C3A', fontWeight: 600, textAlign: 'center' }}>✅ Bid placed successfully!</div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="number" placeholder="₹ Your bid price" value={bidAmounts[detail._id] || ''}
                          onChange={e => updateBid(detail._id, 'price', e.target.value)}
                          style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: `1px solid ${bidErrors[detail._id] ? '#dc2626' : '#E5E7EB'}`, fontSize: 14 }} />
                        <input type="number" placeholder="Qty" value={bidQtys[detail._id] || ''}
                          onChange={e => updateBid(detail._id, 'qty', e.target.value)}
                          style={{ width: 80, padding: '10px 14px', borderRadius: 10, border: `1px solid ${bidErrors[detail._id] ? '#dc2626' : '#E5E7EB'}`, fontSize: 14 }} />
                        <button onClick={() => handleBid(detail._id)} disabled={bidLoading === detail._id}
                          style={{ background: '#1B5C3A', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {bidLoading === detail._id ? 'Placing...' : 'Place Bid'}
                        </button>
                      </div>
                    )}
                    {bidErrors[detail._id] && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6, fontWeight: 600 }}>⚠️ {bidErrors[detail._id]}</div>}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </PublicLayout>
  );
}
